import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import Seo from './components/Seo';
import { canOpen } from './lib/access';


// Context
import { AuthProvider, useAuth } from './context/AuthContext';


// Layouts
import PublicLayout from './layouts/PublicLayout';
import DashboardLayout from './layouts/DashboardLayout';


// Public Pages
import HomePage from './pages/HomePage';
import ServicesPage from './pages/ServicesPage';
import QuotePage from './pages/QuotePage';
import ServiceRequestPage from './pages/ServiceRequestPage';
import AboutPage from './pages/AboutPage';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PartOrderPage from './pages/PartOrderPage';
import CleaningRequestPage from './pages/CleaningRequestPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import NotFoundPage from './pages/NotFoundPage';


// Dashboard Pages
import DashboardPage from './pages/dashboard/DashboardPage';
import ServiceRequestsPage from './pages/dashboard/ServiceRequestsPage';
import QuotesPage from './pages/dashboard/QuotesPage';
import ProjectsPage from './pages/dashboard/ProjectsPage';
import InventoryPage from './pages/dashboard/InventoryPage';
import BillableMaterialsPage from './pages/dashboard/BillableMaterialsPage';
import PurchaseOrdersPage from './pages/dashboard/PurchaseOrdersPage';
import EquipmentPage from './pages/dashboard/EquipmentPage';
import EmployeesPage from './pages/dashboard/EmployeesPage';
import TimeEntriesPage from './pages/dashboard/TimeEntriesPage';
import LeaveRequestsPage from './pages/dashboard/LeaveRequestsPage';
import ProfilePage from './pages/dashboard/ProfilePage';
import SettingsPage from './pages/dashboard/SettingsPage';


// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#00b4d8] border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/connexion" replace />;
  }
  
  return children;
};


// Garde de page : sans la permission requise, retour au tableau de bord.
// Le serveur refuse de toute façon les données (403); ceci évite une page vide.
// Page réservée au rôle Administrateur.
const AdminRoute = ({ children }) => {
  const { isAdmin } = useAuth();
  return isAdmin ? children : <Navigate to="/dashboard" replace />;
};

const PermissionRoute = ({ path, children }) => {
  const { can } = useAuth();
  return canOpen(can, path) ? children : <Navigate to="/dashboard" replace />;
};


// Public Route (redirects to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#00b4d8] border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  return children;
};


function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={
        <PublicRoute>
          <PublicLayout>
            <HomePage />
          </PublicLayout>
        </PublicRoute>
      } />
      <Route path="/services" element={
        <PublicLayout>
          <ServicesPage />
        </PublicLayout>
      } />
      <Route path="/soumission" element={
        <PublicLayout>
          <QuotePage />
        </PublicLayout>
      } />
      <Route path="/demande-service" element={
        <PublicLayout>
          <ServiceRequestPage />
        </PublicLayout>
      } />
      <Route path="/a-propos" element={
        <PublicLayout>
          <AboutPage />
        </PublicLayout>
      } />
      <Route path="/connexion" element={<LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/commande-piece" element={
        <PublicLayout>
          <PartOrderPage />
        </PublicLayout>
      } />
      <Route path="/demande-nettoyage" element={
        <PublicLayout>
          <CleaningRequestPage />
        </PublicLayout>
      } />
      <Route path="/politique-confidentialite" element={
        <PublicLayout>
          <PrivacyPolicyPage />
        </PublicLayout>
      } />


      {/* Dashboard Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardLayout>
            <DashboardPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/dashboard/service-requests" element={
        <ProtectedRoute>
          <PermissionRoute path="/dashboard/service-requests">
            <DashboardLayout>
              <ServiceRequestsPage />
            </DashboardLayout>
          </PermissionRoute>
        </ProtectedRoute>
      } />
      <Route path="/dashboard/quotes" element={
        <ProtectedRoute>
          <PermissionRoute path="/dashboard/quotes">
            <DashboardLayout>
              <QuotesPage />
            </DashboardLayout>
          </PermissionRoute>
        </ProtectedRoute>
      } />
      <Route path="/dashboard/projects" element={
        <ProtectedRoute>
          <PermissionRoute path="/dashboard/projects">
            <DashboardLayout>
              <ProjectsPage />
            </DashboardLayout>
          </PermissionRoute>
        </ProtectedRoute>
      } />
      <Route path="/dashboard/inventory" element={
        <ProtectedRoute>
          <PermissionRoute path="/dashboard/inventory">
            <DashboardLayout>
              <InventoryPage />
            </DashboardLayout>
          </PermissionRoute>
        </ProtectedRoute>
      } />
      <Route path="/dashboard/billable-materials" element={
        <ProtectedRoute>
          <PermissionRoute path="/dashboard/billable-materials">
            <DashboardLayout>
              <BillableMaterialsPage />
            </DashboardLayout>
          </PermissionRoute>
        </ProtectedRoute>
      } />
      <Route path="/dashboard/purchase-orders" element={
        <ProtectedRoute>
          <PermissionRoute path="/dashboard/purchase-orders">
            <DashboardLayout>
              <PurchaseOrdersPage />
            </DashboardLayout>
          </PermissionRoute>
        </ProtectedRoute>
      } />
      <Route path="/dashboard/equipment" element={
        <ProtectedRoute>
          <PermissionRoute path="/dashboard/equipment">
            <DashboardLayout>
              <EquipmentPage />
            </DashboardLayout>
          </PermissionRoute>
        </ProtectedRoute>
      } />
      <Route path="/dashboard/employees" element={
        <ProtectedRoute>
          <PermissionRoute path="/dashboard/employees">
            <DashboardLayout>
              <EmployeesPage />
            </DashboardLayout>
          </PermissionRoute>
        </ProtectedRoute>
      } />
      <Route path="/dashboard/time-entries" element={
        <ProtectedRoute>
          <PermissionRoute path="/dashboard/time-entries">
            <DashboardLayout>
              <TimeEntriesPage />
            </DashboardLayout>
          </PermissionRoute>
        </ProtectedRoute>
      } />
      <Route path="/dashboard/leave-requests" element={
        <ProtectedRoute>
          <PermissionRoute path="/dashboard/leave-requests">
            <DashboardLayout>
              <LeaveRequestsPage />
            </DashboardLayout>
          </PermissionRoute>
        </ProtectedRoute>
      } />
      <Route path="/dashboard/settings" element={
        <ProtectedRoute>
          <AdminRoute>
            <DashboardLayout>
              <SettingsPage />
            </DashboardLayout>
          </AdminRoute>
        </ProtectedRoute>
      } />
      <Route path="/dashboard/profile" element={
        <ProtectedRoute>
          <DashboardLayout>
            <ProfilePage />
          </DashboardLayout>
        </ProtectedRoute>
      } />


      {/* Fallback */}
      <Route path="*" element={
        <PublicLayout>
          <NotFoundPage />
        </PublicLayout>
      } />
    </Routes>
  );
}


function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Seo />
        <AppRoutes />
        <Toaster 
          position="top-right"
          richColors
          closeButton
          toastOptions={{
            style: {
              fontFamily: 'Inter, sans-serif',
            },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}


export default App;
